import { useState } from 'react';
import CameraCapture from '../components/CameraCapture';
import { useExpenses } from '../contexts/ExpenseContext';
import { useSettings } from '../contexts/SettingsContext';
import { BENNETT_EMAILS, getLocalDate } from '../types';
import { jsPDF } from 'jspdf';
import { 
  CheckCircle, 
  Loader2,
  AlertCircle,
  ChevronLeft,
  Send,
  Save,
  Camera,
  Plus,
  X,
  Paperclip,
  Mail,
  Trash2,
  Download
} from 'lucide-react';

interface CapturedDocument {
  id: string;
  blob: Blob;
  filename: string;
  freightBillNumber: string;
  preview: string;
}

export default function BOLSend() {
  const { addExpense } = useExpenses();
  const { settings } = useSettings();
  
  const [step, setStep] = useState<'camera' | 'details' | 'success'>('camera');
  const [documents, setDocuments] = useState<CapturedDocument[]>([]);
  const [currentCapture, setCurrentCapture] = useState<{ blob: Blob; filename: string } | null>(null);
  const [currentFreightBill, setCurrentFreightBill] = useState('');
  const [lastFreightBill, setLastFreightBill] = useState(''); // Persist FB# across captures
  
  // Multi-select emails - start with none selected (user picks)
  const [selectedEmails, setSelectedEmails] = useState<string[]>([]);
  const [customEmail, setCustomEmail] = useState('');
  const [customEmails, setCustomEmails] = useState<string[]>([]);
  
  const [date, setDate] = useState(getLocalDate());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [lastAction, setLastAction] = useState<'save' | 'send' | 'both'>('both');

  const handleCapture = (blob: Blob, filename: string) => {
    setCurrentCapture({ blob, filename });
    // Pre-populate with last FB# if available (same job)
    if (lastFreightBill && !currentFreightBill) {
      setCurrentFreightBill(lastFreightBill);
    }
    setStep('details');
  };

  const handleAddDocument = () => {
    if (!currentCapture || !currentFreightBill) return;
    
    const newDoc: CapturedDocument = {
      id: crypto.randomUUID(),
      blob: currentCapture.blob,
      filename: currentCapture.filename,
      freightBillNumber: currentFreightBill,
      preview: URL.createObjectURL(currentCapture.blob)
    };
    
    setDocuments(prev => [...prev, newDoc]);
    setLastFreightBill(currentFreightBill); // Remember for next doc
    setCurrentCapture(null);
    setCurrentFreightBill('');
    setStep('camera');
  };

  const handleRemoveDocument = (id: string) => {
    setDocuments(prev => {
      const doc = prev.find(d => d.id === id);
      if (doc) URL.revokeObjectURL(doc.preview);
      return prev.filter(d => d.id !== id);
    });
  };

  const handleCancelCapture = () => {
    if (documents.length > 0) {
      setCurrentCapture(null);
      setCurrentFreightBill('');
      setStep('details');
    } else {
      window.history.back();
    }
  };

  const toggleBennettEmail = (email: string) => {
    setSelectedEmails(prev => 
      prev.includes(email)
        ? prev.filter(e => e !== email)
        : [...prev, email]
    );
  };

  const addCustomEmail = () => {
    const trimmed = customEmail.trim().toLowerCase();
    if (trimmed && trimmed.includes('@') && !customEmails.includes(trimmed)) {
      setCustomEmails(prev => [...prev, trimmed]);
      setCustomEmail('');
    }
  };

  const removeCustomEmail = (email: string) => {
    setCustomEmails(prev => prev.filter(e => e !== email));
  };

  const getAllRecipients = () => {
    return [...selectedEmails, ...customEmails];
  };

  const generateEmailLink = () => {
    const allEmails = getAllRecipients();
    const freightBills = documents.map(d => d.freightBillNumber).join(', ');
    const subject = encodeURIComponent(`Freight Bill# ${freightBills}`);
    const body = encodeURIComponent(
      `Delivered\nTruck# ${settings.truckNumber}\nDriver ID: ${settings.driverId}\n\nFreight Bills: ${freightBills}\nAttachments: ${documents.length} document(s)\n\nSent from Tax Expense Tracker`
    );
    // Use first email as "to", rest as "cc"
    const [primaryEmail, ...ccEmails] = allEmails;
    let mailto = `mailto:${primaryEmail}?subject=${subject}&body=${body}`;
    if (ccEmails.length > 0) {
      mailto += `&cc=${ccEmails.join(',')}`;
    }
    return mailto;
  };

  const handleSaveAll = async () => {
    if (documents.length === 0) return false;

    setIsSubmitting(true);
    setError('');

    try {
      for (const doc of documents) {
        // Convert to PDF before saving
        const pdfBlob = await convertToPdf(doc.blob);
        const bolFilename = `BOL${doc.freightBillNumber}_${date.replace(/-/g, '')}.pdf`;
        
        await addExpense({
          category: 'bol',
          description: `Freight Bill# ${doc.freightBillNumber}`,
          date,
          file: pdfBlob,
          filename: bolFilename,
          freightBillNumber: doc.freightBillNumber,
        });
      }
      return true;
    } catch (err) {
      setError('Failed to save BOLs. Please try again.');
      console.error(err);
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSaveOnly = async () => {
    setLastAction('save');
    const success = await handleSaveAll();
    if (success) {
      setStep('success');
    }
  };

  const handleSendOnly = async () => {
    setLastAction('send');
    await openEmailWithOptions();
  };

  const handleSaveAndSend = async () => {
    setLastAction('both');
    const success = await handleSaveAll();
    if (success) {
      await openEmailWithOptions();
      setStep('success');
    }
  };

  const openEmailWithOptions = async () => {
    const freightBills = documents.map(d => d.freightBillNumber).join(', ');
    const allRecipients = getAllRecipients();
    const subject = `Freight Bill# ${freightBills}`;
    
    // Include recipients in body for easy copy when using Share API
    const recipientLine = allRecipients.length > 0 
      ? `📧 SEND TO: ${allRecipients.join(', ')}\n\n` 
      : '';
    
    const body = `${recipientLine}Delivered\nTruck# ${settings.truckNumber}\nDriver ID: ${settings.driverId}\n\nFreight Bills: ${freightBills}\nAttachments: ${documents.length} document(s)\n\nSent from Tax Expense Tracker`;
    
    // Try Web Share API first (works on mobile, can include files!)
    if (navigator.share && navigator.canShare) {
      try {
        // Convert images to PDFs for sharing
        const files = await Promise.all(
          documents.map(async (doc) => {
            const pdfBlob = await convertToPdf(doc.blob);
            const filename = `BOL_${doc.freightBillNumber}.pdf`;
            return new File([pdfBlob], filename, { type: 'application/pdf' });
          })
        );
        
        // Check if the device can share files
        const shareData = {
          title: subject,
          text: body,
          files: files
        };
        
        if (navigator.canShare(shareData)) {
          await navigator.share(shareData);
          return;
        }
      } catch (err) {
        console.log('Web Share failed, falling back to mailto:', err);
      }
    }
    
    // Fallback to mailto: (no attachments, but fills in To/Subject/Body)
    const mailtoLink = generateEmailLink();
    window.location.href = mailtoLink;
  };

  // Convert image blob to PDF
  const convertToPdf = async (imageBlob: Blob): Promise<Blob> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        try {
          // Create PDF with image dimensions
          const orientation = img.width > img.height ? 'landscape' : 'portrait';
          const pdf = new jsPDF({
            orientation,
            unit: 'px',
            format: [img.width, img.height]
          });
          
          // Add image to PDF
          const canvas = document.createElement('canvas');
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext('2d');
          if (!ctx) {
            reject(new Error('Could not get canvas context'));
            return;
          }
          ctx.drawImage(img, 0, 0);
          const imgData = canvas.toDataURL('image/jpeg', 0.9);
          
          pdf.addImage(imgData, 'JPEG', 0, 0, img.width, img.height);
          
          const pdfBlob = pdf.output('blob');
          resolve(pdfBlob);
        } catch (err) {
          reject(err);
        }
      };
      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = URL.createObjectURL(imageBlob);
    });
  };

  const handleDownloadImages = async () => {
    // Download each document as a PDF file
    for (const doc of documents) {
      try {
        const pdfBlob = await convertToPdf(doc.blob);
        const url = URL.createObjectURL(pdfBlob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `BOL_${doc.freightBillNumber}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } catch (err) {
        console.error('PDF conversion error:', err);
      }
    }
  };

  const resetForm = () => {
    // Clean up blob URLs
    documents.forEach(doc => URL.revokeObjectURL(doc.preview));
    
    setStep('camera');
    setDocuments([]);
    setCurrentCapture(null);
    setCurrentFreightBill('');
    setLastFreightBill('');
    setSelectedEmails([]);
    setCustomEmails([]);
    setCustomEmail('');
    setDate(getLocalDate());
    setError('');
  };

  if (step === 'success') {
    return (
      <div className="max-w-md mx-auto text-center py-20 animate-fade-in">
        <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
          <CheckCircle className="w-10 h-10 text-green-400" />
        </div>
        <h2 className="text-2xl font-bold text-white mb-2">
          {lastAction === 'both' ? 'Saved & Email Opened!' : `${documents.length} BOL(s) Saved!`}
        </h2>
        <p className="text-white/60 mb-6">
          {lastAction === 'both' 
            ? 'Add recipients from the email body, then send!'
            : 'BOLs have been saved to your expenses'}
        </p>
        <button
          onClick={resetForm}
          className="inline-flex items-center space-x-2 px-6 py-3 bg-shuttle-sand text-shuttle-dark rounded-xl font-medium hover:bg-amber-400 transition-colors"
        >
          <Camera className="w-5 h-5" />
          <span>Scan More BOLs</span>
        </button>
      </div>
    );
  }

  if (step === 'camera') {
    return (
      <div className="max-w-2xl mx-auto animate-fade-in">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-white">Scan BOL</h1>
          <p className="text-white/60 mt-1">
            {documents.length > 0 
              ? `${documents.length} document(s) ready • Scan another or proceed`
              : 'Scan your Bill of Lading to save and email to Bennett'}
          </p>
        </div>
        
        {/* Show existing documents */}
        {documents.length > 0 && (
          <div className="mb-4 p-4 bg-white/10 backdrop-blur-md rounded-xl border border-white/20">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-medium text-white/80 flex items-center space-x-2">
                <Paperclip className="w-4 h-4" />
                <span>Documents Ready ({documents.length})</span>
              </h3>
              <button
                onClick={() => {
                  setCurrentCapture(null);
                  setStep('details');
                }}
                className="text-sm text-shuttle-sand hover:text-amber-400 transition-colors"
              >
                Review & Send →
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {documents.map(doc => (
                <div key={doc.id} className="px-3 py-1 bg-white/10 rounded-lg text-sm text-white/80">
                  FB# {doc.freightBillNumber}
                </div>
              ))}
            </div>
          </div>
        )}
        
        <CameraCapture onCapture={handleCapture} onCancel={handleCancelCapture} />
      </div>
    );
  }

  // Details step - can be viewing current capture OR reviewing all documents
  return (
    <div className="max-w-2xl mx-auto animate-fade-in">
      <button
        onClick={() => {
          if (currentCapture) {
            setCurrentCapture(null);
            setCurrentFreightBill('');
          }
          setStep('camera');
        }}
        className="flex items-center space-x-2 text-white/70 hover:text-white mb-6 transition-colors"
      >
        <ChevronLeft className="w-5 h-5" />
        <span>{currentCapture ? 'Retake Photo' : 'Add More'}</span>
      </button>

      <div className="bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 overflow-hidden">
        {/* Current Image Preview (if capturing) */}
        {currentCapture && (
          <>
            <div className="aspect-video bg-black flex items-center justify-center">
              <img
                src={URL.createObjectURL(currentCapture.blob)}
                alt="Captured BOL"
                className="max-h-full max-w-full object-contain"
              />
            </div>
            
            <div className="p-4 border-b border-white/10">
              <label htmlFor="currentFreightBill" className="block text-sm font-medium text-white/80 mb-2">
                Freight Bill # <span className="text-red-400">*</span>
              </label>
              <div className="flex gap-2">
                <input
                  id="currentFreightBill"
                  type="text"
                  inputMode="numeric"
                  value={currentFreightBill}
                  onChange={(e) => setCurrentFreightBill(e.target.value)}
                  placeholder="9954711"
                  className="flex-1 min-w-0 px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40 focus:ring-2 focus:ring-shuttle-sand transition-all text-lg font-mono"
                  style={{ fontSize: '16px' }}
                />
                <button
                  onClick={handleAddDocument}
                  disabled={!currentFreightBill}
                  className="flex-shrink-0 px-5 py-3 bg-shuttle-sand text-shuttle-dark rounded-xl font-bold hover:bg-amber-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                >
                  <Plus className="w-5 h-5" />
                  <span>Add</span>
                </button>
              </div>
              {lastFreightBill && currentFreightBill === lastFreightBill && (
                <p className="text-xs text-shuttle-sand mt-1">✓ Same FB# as previous</p>
              )}
            </div>
          </>
        )}

        {/* All Documents List */}
        {documents.length > 0 && (
          <div className="p-4 border-b border-white/10">
            <h3 className="text-sm font-medium text-white/80 mb-3 flex items-center space-x-2">
              <Paperclip className="w-4 h-4" />
              <span>Documents to Send ({documents.length})</span>
            </h3>
            <div className="space-y-2 max-h-40 overflow-y-auto">
              {documents.map(doc => (
                <div key={doc.id} className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <img 
                      src={doc.preview} 
                      alt={`FB# ${doc.freightBillNumber}`}
                      className="w-12 h-12 object-cover rounded"
                    />
                    <span className="text-white font-mono">FB# {doc.freightBillNumber}</span>
                  </div>
                  <button
                    onClick={() => handleRemoveDocument(doc.id)}
                    className="p-2 hover:bg-red-500/20 rounded-lg transition-colors"
                  >
                    <Trash2 className="w-4 h-4 text-red-400" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Form - only show when not adding current capture OR when we have documents */}
        {(!currentCapture && documents.length > 0) && (
          <div className="p-6 space-y-6">
            {error && (
              <div className="flex items-center space-x-2 p-4 bg-red-500/20 border border-red-500/50 rounded-xl text-red-300 text-sm">
                <AlertCircle className="w-5 h-5 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}

            {/* Bennett Terminals (Multi-select) */}
            <div>
              <label className="block text-sm font-medium text-white/80 mb-2">
                <Mail className="w-4 h-4 inline mr-2" />
                Select Terminals
              </label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-48 overflow-y-auto p-2 bg-white/5 rounded-xl">
                {BENNETT_EMAILS.map((item) => (
                  <label
                    key={item.code}
                    className={`flex items-center space-x-2 p-2 rounded-lg cursor-pointer transition-all ${
                      selectedEmails.includes(item.email)
                        ? 'bg-shuttle-sand/20 border border-shuttle-sand'
                        : 'bg-white/5 border border-transparent hover:bg-white/10'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedEmails.includes(item.email)}
                      onChange={() => toggleBennettEmail(item.email)}
                      className="w-4 h-4 rounded border-white/20 text-shuttle-sand focus:ring-shuttle-sand bg-white/10"
                    />
                    <span className="text-sm text-white">{item.code.toUpperCase()}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Custom Email Addresses */}
            <div>
              <label className="block text-sm font-medium text-white/80 mb-2">
                Additional Email Addresses
              </label>
              <div className="flex space-x-2 mb-2">
                <input
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  autoCapitalize="off"
                  autoCorrect="off"
                  spellCheck={false}
                  value={customEmail}
                  onChange={(e) => setCustomEmail(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addCustomEmail();
                    }
                  }}
                  placeholder="email@example.com"
                  className="flex-1 px-4 py-3 bg-shuttle-dark border border-white/30 rounded-xl text-white placeholder-white/40 focus:ring-2 focus:ring-shuttle-sand focus:border-shuttle-sand transition-all text-base"
                  style={{ fontSize: '16px' }} /* Prevents iOS zoom */
                />
                <button
                  type="button"
                  onClick={addCustomEmail}
                  className="px-4 py-3 bg-shuttle-sand text-shuttle-dark rounded-xl hover:bg-amber-400 transition-colors font-medium"
                >
                  <Plus className="w-5 h-5" />
                </button>
              </div>
              {customEmails.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {customEmails.map(email => (
                    <span
                      key={email}
                      className="inline-flex items-center space-x-1 px-3 py-1 bg-blue-500/20 border border-blue-500/50 rounded-full text-sm text-blue-300"
                    >
                      <span>{email}</span>
                      <button
                        onClick={() => removeCustomEmail(email)}
                        className="hover:text-white transition-colors"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Email Preview */}
            <div className="bg-white/5 rounded-xl p-4 border border-white/10">
              <h3 className="text-sm font-medium text-white/80 mb-3">📧 Email Preview</h3>
              <div className="space-y-2 text-sm">
                <p className="text-white/60">
                  <span className="text-white/40">To:</span>{' '}
                  <span className="text-white/80">
                    {getAllRecipients().length > 0 
                      ? getAllRecipients().join(', ') 
                      : 'Select at least one recipient'}
                  </span>
                </p>
                <p className="text-white/60">
                  <span className="text-white/40">Subject:</span> Freight Bill# {documents.map(d => d.freightBillNumber).join(', ') || '________'}
                </p>
                <div className="border-t border-white/10 pt-2 mt-2">
                  <p className="text-white/80">Delivered</p>
                  <p className="text-white/80">Truck# {settings.truckNumber}</p>
                  <p className="text-white/80">Driver ID: {settings.driverId}</p>
                  <p className="text-white/60 mt-2">Attachments: {documents.length} document(s)</p>
                </div>
              </div>
            </div>

            {/* Date */}
            <div>
              <label htmlFor="date" className="block text-sm font-medium text-white/80 mb-1">
                Date
              </label>
              <input
                id="date"
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white focus:ring-2 focus:ring-shuttle-sand focus:border-shuttle-sand transition-all"
              />
            </div>

            {/* Action Buttons */}
            <div className="space-y-3">
              <button
                onClick={handleSaveAndSend}
                disabled={documents.length === 0 || getAllRecipients().length === 0 || isSubmitting}
                className="w-full flex items-center justify-center space-x-2 py-4 px-6 bg-gradient-to-r from-shuttle-sand to-amber-500 text-shuttle-dark rounded-xl font-medium hover:from-amber-400 hover:to-amber-500 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span>Saving {documents.length} BOL(s)...</span>
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    <span>Save & Send Email ({documents.length})</span>
                  </>
                )}
              </button>

              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={handleSaveOnly}
                  disabled={documents.length === 0 || isSubmitting}
                  className="flex items-center justify-center space-x-2 py-3 px-4 bg-white/10 border border-white/20 text-white rounded-xl font-medium hover:bg-white/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Save className="w-4 h-4" />
                  <span>Save Only</span>
                </button>

                <button
                  onClick={handleSendOnly}
                  disabled={documents.length === 0 || getAllRecipients().length === 0}
                  className="flex items-center justify-center space-x-2 py-3 px-4 bg-white/10 border border-white/20 text-white rounded-xl font-medium hover:bg-white/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Send className="w-4 h-4" />
                  <span>Send Only</span>
                </button>
              </div>

              {/* Download button for desktop users */}
              <button
                onClick={handleDownloadImages}
                disabled={documents.length === 0}
                className="w-full flex items-center justify-center space-x-2 py-2 text-shuttle-sand hover:text-amber-400 transition-all disabled:opacity-50 text-sm"
              >
                <Download className="w-4 h-4" />
                <span>Download PDFs for Manual Attach</span>
              </button>
            </div>

            <p className="text-xs text-white/40 text-center">
              📄 PDFs attached via Share. Copy recipient emails from message body to "To:" field.
            </p>
          </div>
        )}

        {/* Show message if no documents yet and no current capture */}
        {!currentCapture && documents.length === 0 && (
          <div className="p-12 text-center">
            <Camera className="w-12 h-12 text-white/30 mx-auto mb-4" />
            <p className="text-white/60">No documents captured yet</p>
            <button
              onClick={() => setStep('camera')}
              className="mt-4 px-4 py-2 bg-shuttle-sand text-shuttle-dark rounded-lg font-medium hover:bg-amber-400 transition-colors"
            >
              Start Scanning
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
